import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import {
  BasePessoaFisicaUseCase,
  type FetchResult,
} from '@/features/proposta/entities/Comprador/use-case/base-pessoa-fisica-use-case'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { formatRadios } from '@/features/proposta/utils/format-radios'
import type { TCliente } from '@/types/cliente'
import { origemTelefone } from '@/utils/schemas/telefone.schema'

export class ClienteFormMapper extends BasePessoaFisicaUseCase<CompradorPessoaFisicaStepSchemaInput> {
  constructor(
    private readonly clienteAtendimento: { id: string; cpf?: string | null },
    private readonly emailsJaVinculados: Record<string, string>,
    private readonly nacionalidadePrincipalId: string,
  ) {
    super()
  }

  private obterPatchMenorIdade(cliente: TCliente, inativo?: boolean): CompradorPessoaFisicaStepSchemaInput {
    return {
      tipoDoCliente: cliente.tipoDoCliente,
      tipo: 'PessoaFisica',
      mode: 'create',
      dados: {
        id: inativo ? '' : cliente.id,
        cpf: cliente.cpf ?? '',
        nome: cliente.nome,
        profissaoId: cliente.profissao?.id ?? '',
        nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
        maiorDe18Anos: formatRadios(cliente.maiorDe18Anos),
        buscouCpf: !!cliente.cpf,
        email: cliente.email,
      },
      estadoCivil: null,
      contato: null,
      conjuge: null,
      emailsJaVinculados: this.emailsJaVinculados,
    }
  }

  private obterPatchMaiorIdade(cliente: TCliente, inativo?: boolean): CompradorPessoaFisicaStepSchemaInput {
    return {
      tipoDoCliente: cliente.tipoDoCliente,
      tipo: 'PessoaFisica',
      mode: 'create',
      dados: {
        id: inativo ? '' : cliente.id,
        cpf: cliente.cpf ?? '',
        nome: cliente.nome,
        profissaoId: cliente.profissao?.id ?? '',
        nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
        maiorDe18Anos: formatRadios(cliente.maiorDe18Anos),
        buscouCpf: !!cliente.cpf,
        email: null,
      },
      estadoCivil: {
        status: cliente.estadoCivil ?? undefined,
        regimeCasamento: cliente.regimeCasamento,
        tipoAssinatura: undefined,
        uniaoEstavel: formatRadios(cliente.possuiUniaoEstavel),
      },
      contato: {
        email: cliente.email ?? '',
        telefone: {
          origem: cliente.telefone?.origem ?? origemTelefone.Brasil,
          numero: cliente.telefone?.numero ?? '',
          principal: true,
        },
      },
      conjuge: null,
      emailsJaVinculados: this.emailsJaVinculados,
    }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TCliente, AxiosError>,
    compradorId?: string,
  ): FetchResult<Partial<CompradorPessoaFisicaStepSchemaInput>> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) {
      const shouldOpenModal = this.clienteAtendimento.id === compradorId && !this.clienteAtendimento.cpf
      return { type: 'NOT_FOUND', shouldOpenModal }
    }
    if (result.isSuccess && result.data) {
      const cliente = result.data
      return {
        type: 'SUCCESS',
        patch: this.obterPatchCliente(cliente),
      }
    }

    return { type: 'ERROR' }
  }

  interpretarResultadoBuscaAutomatica(
    result: QueryObserverResult<TCliente, AxiosError>,
    clienteInativo?: TCliente,
  ): FetchResult<Partial<CompradorPessoaFisicaStepSchemaInput>> {
    if (clienteInativo) {
      return {
        type: 'INACTIVE',
        patch: this.obterPatchCliente(clienteInativo, true),
      }
    }
    if (result.isSuccess && result.data) {
      const cliente = result.data
      return {
        type: 'SUCCESS',
        patch: this.obterPatchCliente(cliente),
      }
    }
    return { type: 'ERROR' }
  }

  obterPatchCliente(cliente: TCliente, inativo?: boolean): CompradorPessoaFisicaStepSchemaInput {
    const isMenor = cliente.maiorDe18Anos === false
    return isMenor ? this.obterPatchMenorIdade(cliente, inativo) : this.obterPatchMaiorIdade(cliente, inativo)
  }

  limparFormulario(): CompradorPessoaFisicaStepSchemaInput {
    return {
      tipoDoCliente: 'Contato',
      tipo: 'PessoaFisica',
      mode: 'create',
      dados: {
        cpf: '',
        nome: '',
        profissaoId: '',
        nacionalidadeId: this.nacionalidadePrincipalId,
        maiorDe18Anos: '',
        buscouCpf: false,
        email: null,
      },
      contato: {
        email: '',
        telefone: { numero: '', origem: origemTelefone.Brasil, principal: true },
      },
      estadoCivil: {
        regimeCasamento: null,
        status: null,
        tipoAssinatura: undefined,
        uniaoEstavel: undefined,
      },
      conjuge: null,
      emailsJaVinculados: this.emailsJaVinculados,
    }
  }
}
